import React, { useState, useEffect } from 'react';
import { Button } from '../common/Button';
import { Pnevmatika, CreatePnevmatikaData, UpdatePnevmatikaData } from '../../types/pnevmatika';
import { formatPhoneNumber, normalizePhoneNumber } from '../../utils/phoneUtils';
import { printCertificate } from '../../utils/printCertificate';
import './PnevmatikaForm.css';
import { pnevmatikeApi } from '../../services/api';

interface PnevmatikaFormProps {
  pnevmatika?: Pnevmatika; // undefined for add, Pnevmatika for edit
  onSubmit: (data: CreatePnevmatikaData | UpdatePnevmatikaData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

type FormState = {
  ime: string;
  priimek: string;
  telefonska_stevilka: string;
  oznaka_gume: string;
  spravljeno_od: string;
  z_platisci: boolean;
  letna: boolean;
  placani_znesek: string;
  komplet: '1' | '2' | '3' | '4' | '4+1' | '4+2' | '4+3' | '4+4';
  pripisek: string;
  znamka: string;
};

export const PnevmatikaForm: React.FC<PnevmatikaFormProps> = ({
  pnevmatika,
  onSubmit,
  onCancel,
  loading = false,
}: PnevmatikaFormProps) => {
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  // Inverted default requested: in winter months default should be Letna (true),
  // in summer months default should be Zimska (false).
  const initialLetna = (() => { const m = today.getMonth() + 1; return !(m >= 3 && m <= 9); })();
  const [formData, setFormData] = useState<FormState>({
    ime: '',
    priimek: '',
    telefonska_stevilka: '',
    oznaka_gume: '',
    spravljeno_od: todayStr, // Today's date
    z_platisci: false,
    letna: initialLetna,
    placani_znesek: '',
    komplet: '4',
  pripisek: '',
  znamka: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Pre-fill form if editing
  useEffect(() => {
  if (pnevmatika) {
    setFormData({
      ime: pnevmatika.ime,
      priimek: pnevmatika.priimek,
      telefonska_stevilka: pnevmatika.telefonska_stevilka || '',
      oznaka_gume: pnevmatika.oznaka_gume.toString(),
      spravljeno_od: new Date(pnevmatika.spravljeno_od).toISOString().split('T')[0],
      z_platisci: pnevmatika.z_platisci,
      letna: pnevmatika.letna,
      placani_znesek: pnevmatika.placani_znesek || '0.00',
      komplet: pnevmatika.komplet || '4',
  pripisek: pnevmatika.pripisek || '',
  znamka: pnevmatika.znamka || '',
    });
  }
  }, [pnevmatika]);

  // When adding a new pnevmatika, auto-suggest the next available oznaka
  useEffect(() => {
    let active = true;
    (async () => {
      if (!pnevmatika) {
        try {
          const next = await pnevmatikeApi.getNextOznaka();
          if (active) {
            setFormData((prev: FormState) => ({ ...prev, oznaka_gume: String(next) }));
          }
        } catch (e) {
          // silently ignore; user can enter manually
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [pnevmatika]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.ime.trim()) {
      newErrors.ime = 'Ime je obvezno';
    }

    if (!formData.priimek.trim()) {
      newErrors.priimek = 'Priimek je obvezen';
    }

    // Phone optional; if provided, must be valid
    if (formData.telefonska_stevilka && formData.telefonska_stevilka.trim() !== '') {
      const cleaned = formData.telefonska_stevilka.replace(/\D/g, '');
      if (cleaned.length !== 9) {
        newErrors.telefonska_stevilka = 'Telefonska številka mora vsebovati natanko 9 številk';
      }
    }

    if (!formData.oznaka_gume.trim()) {
      newErrors.oznaka_gume = 'Oznaka pnevmatike je obvezna';
    } else {
      const oznaka = parseInt(formData.oznaka_gume);
      if (isNaN(oznaka) || oznaka < 0) {
        newErrors.oznaka_gume = 'Oznaka mora biti pozitivno število';
      }
    }

    if (!formData.spravljeno_od) {
      newErrors.spravljeno_od = 'Datum je obvezen';
    }

    // plačani znesek required and >= 0
    if (formData.placani_znesek === undefined || formData.placani_znesek === null || formData.placani_znesek.toString().trim() === '') {
      newErrors.placani_znesek = 'Plačani znesek je obvezen';
    } else {
      const znesek = parseFloat(formData.placani_znesek);
      if (isNaN(znesek) || znesek < 0) {
        newErrors.placani_znesek = 'Plačani znesek mora biti število ≥ 0';
      }
    }

    // komplet required
    if (!formData.komplet) {
      newErrors.komplet = 'Komplet je obvezen';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const submitData: CreatePnevmatikaData | UpdatePnevmatikaData = {
        ime: formData.ime.trim(),
        priimek: formData.priimek.trim(),
        telefonska_stevilka: formData.telefonska_stevilka.trim() ? normalizePhoneNumber(formData.telefonska_stevilka) : null,
        oznaka_gume: parseInt(formData.oznaka_gume),
        spravljeno_od: formData.spravljeno_od,
        z_platisci: formData.z_platisci,
        letna: formData.letna,
        placani_znesek: parseFloat(formData.placani_znesek),
        komplet: formData.komplet,
  pripisek: formData.pripisek.trim() || null,
  znamka: formData.znamka.trim() || null,
      };

      await onSubmit(submitData);
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const handleInputChange = (field: keyof FormState, value: string | boolean) => {
    let processedValue: string | boolean = value;
    
    // Format phone number as user types
    if (field === 'telefonska_stevilka' && typeof value === 'string') {
      processedValue = formatPhoneNumber(value);
    }

    // If date changes and letna not manually toggled, recompute with inverted default
    if (field === 'spravljeno_od' && typeof value === 'string') {
      const month = new Date(value).getMonth() + 1;
      const defaultLetna = !(month >= 3 && month <= 9);
      setFormData((prev: FormState) => ({ ...prev, letna: defaultLetna }));
    }
    
    setFormData((prev: FormState) => ({ ...prev, [field]: processedValue as any }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev: Record<string, string>) => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="pnevmatika-form pnevmatika-form--two-cols">
      <div className="form-cols">
        <div className="form-col">
          <div className="form-group">
            <label htmlFor="ime">Ime *</label>
            <input
              type="text"
              id="ime"
              value={formData.ime}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('ime', e.target.value)}
              className={errors.ime ? 'error' : ''}
              disabled={loading}
            />
            {errors.ime && <span className="error-text">{errors.ime}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="priimek">Priimek *</label>
            <input
              type="text"
              id="priimek"
              value={formData.priimek}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('priimek', e.target.value)}
              className={errors.priimek ? 'error' : ''}
              disabled={loading}
            />
            {errors.priimek && <span className="error-text">{errors.priimek}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="telefonska_stevilka">Telefonska številka</label>
            <input
              type="tel"
              id="telefonska_stevilka"
              value={formData.telefonska_stevilka}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('telefonska_stevilka', e.target.value)}
              className={errors.telefonska_stevilka ? 'error' : ''}
              placeholder="040 123 456"
              disabled={loading}
            />
            {errors.telefonska_stevilka && <span className="error-text">{errors.telefonska_stevilka}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="oznaka_gume">Oznaka pnevmatike *</label>
            <input
              type="number"
              id="oznaka_gume"
              value={formData.oznaka_gume}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('oznaka_gume', e.target.value)}
              className={errors.oznaka_gume ? 'error' : ''}
              min="0"
              disabled={loading}
            />
            {errors.oznaka_gume && <span className="error-text">{errors.oznaka_gume}</span>}
          </div>
          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={formData.z_platisci}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('z_platisci', e.target.checked)}
                disabled={loading}
              />{' '}
              Z platišči
            </label>
          </div>
          <div className="form-group">
            <label>Tip</label>
            <div className="radio-group">
              <label>
                <input
                  type="radio"
                  name="sezona"
                  checked={formData.letna === true}
                  onChange={() => handleInputChange('letna', true)}
                  disabled={loading}
                />{' '}
                Letna
              </label>
              <label>
                <input
                  type="radio"
                  name="sezona"
                  checked={formData.letna === false}
                  onChange={() => handleInputChange('letna', false)}
                  disabled={loading}
                />{' '}
                Zimska
              </label>
            </div>
          </div>
        </div>
        <div className="form-col">
          <div className="form-group">
            <label htmlFor="spravljeno_od">Spravljeno od *</label>
            <input
              type="date"
              id="spravljeno_od"
              value={formData.spravljeno_od}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('spravljeno_od', e.target.value)}
              className={errors.spravljeno_od ? 'error' : ''}
              disabled={loading}
            />
            {errors.spravljeno_od && <span className="error-text">{errors.spravljeno_od}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="placani_znesek">Plačani znesek *</label>
            <input
              type="number"
              id="placani_znesek"
              value={formData.placani_znesek}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('placani_znesek', e.target.value)}
              className={errors.placani_znesek ? 'error' : ''}
              min="0"
              step="0.01"
              inputMode="decimal"
              disabled={loading}
            />
            {errors.placani_znesek && <span className="error-text">{errors.placani_znesek}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="komplet">Komplet *</label>
            <select
              id="komplet"
              value={formData.komplet}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleInputChange('komplet', e.target.value as FormState['komplet'])}
              className={errors.komplet ? 'error' : ''}
              disabled={loading}
            >
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="4+1">4+1</option>
              <option value="4+2">4+2</option>
              <option value="4+3">4+3</option>
              <option value="4+4">4+4</option>
            </select>
            {errors.komplet && <span className="error-text">{errors.komplet}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="znamka">Znamka</label>
            <input
              type="text"
              id="znamka"
              value={formData.znamka}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('znamka', e.target.value)}
              className={errors.znamka ? 'error' : ''}
              disabled={loading}
            />
            {errors.znamka && <span className="error-text">{errors.znamka}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="pripisek">Opomba</label>
            <textarea
              id="pripisek"
              value={formData.pripisek}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('pripisek', e.target.value)}
              className={errors.pripisek ? 'error' : ''}
              rows={3}
              disabled={loading}
              style={{ resize: 'vertical' }}
            />
            {errors.pripisek && <span className="error-text">{errors.pripisek}</span>}
          </div>
        </div>
      </div>
      <div className="form-actions">
        <Button
          type="button"
          variant="secondary"
          onClick={() => printCertificate({ ime: formData.ime, priimek: formData.priimek, oznaka_gume: formData.oznaka_gume || '' })}
          disabled={loading}
        >
          Natisni potrdilo
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={loading}>
          Prekliči
        </Button>
        <Button type="submit" loading={loading}>
          Shrani
        </Button>
      </div>
    </form>
  );
};
